import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaFolderOpen, FaUserPlus } from "react-icons/fa";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import {
  LdsOverviewHero,
  LdsOverviewPage,
} from "../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import http from "../api/apiClient";
import { useCan } from "../authz/useCan";
import { formatDutchDate } from "../utils/dutchDate";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";

type ParentContact = { name?: string | null; email?: string | null; phone?: string | null };

type NewFamilyRow = {
  familyId: string;
  familyName?: string | null;
  createdAt?: string | null;
  parents?: ParentContact[];
  children?: string[];
};

const PERIODS = [30, 60, 90];
const DEFAULT_PERIOD = 30;

export default function ReportsNewFamilies(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The report itself needs no family access; opening the full family record does.
  const canOpenFamily = useCan("families", "read");
  const [days, setDays] = useState(DEFAULT_PERIOD);
  const [families, setFamilies] = useState<NewFamilyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message?: string; status?: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    http.get("/reports/families/new", { params: { days } })
      .then(({ data }) => {
        if (!cancelled) setFamilies(Array.isArray(data) ? data : []);
      })
      .catch((caught) => {
        if (!cancelled) setError(mapHttpErrorToMessage(caught, t));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, t]);

  if (error) {
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaUserPlus}
        eyebrow={t("reports.title", { defaultValue: "Reports" })}
        title={t("reports.new_families.title", { defaultValue: "New families" })}
        description={t("reports.new_families.description", {
          defaultValue: "Recently added families, for example to prepare a welcome package.",
        })}
      />

      <LdsTableCard className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">
              {t("reports.new_families.overview", { defaultValue: "New families overview" })}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t("common.result_count", { count: families.length, defaultValue: "{{count}} results" })}
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
            {t("reports.new_families.period", { defaultValue: "Period" })}
            <select
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white"
            >
              {PERIODS.map((period) => (
                <option key={period} value={period}>
                  {t("reports.new_families.last_days", { count: period, defaultValue: "Last {{count}} days" })}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <LdsTableScroll>
            <LdsTable className="min-w-[760px]">
              <LdsTableHead>
                <LdsTableRow>
                  <LdsTableHeadCell>{t("reports.new_families.columns.family", { defaultValue: "Family" })}</LdsTableHeadCell>
                  <LdsTableHeadCell>{t("reports.new_families.columns.parents", { defaultValue: "Parent(s)" })}</LdsTableHeadCell>
                  <LdsTableHeadCell>{t("reports.new_families.columns.children", { defaultValue: "Children" })}</LdsTableHeadCell>
                  <LdsTableHeadCell>{t("reports.new_families.columns.registered", { defaultValue: "Registration date" })}</LdsTableHeadCell>
                  <LdsTableHeadCell>{t("reports.new_families.columns.contact", { defaultValue: "Contact details" })}</LdsTableHeadCell>
                  {canOpenFamily && (
                    <LdsTableHeadCell className="text-right">
                      {t("reports.families.columns.actions", { defaultValue: "Actions" })}
                    </LdsTableHeadCell>
                  )}
                </LdsTableRow>
              </LdsTableHead>
              <LdsTableBody>
                {families.length > 0 ? (
                  families.map((family) => {
                    const parents = family.parents || [];
                    return (
                      <LdsTableRow key={family.familyId}>
                        <LdsTableCell className="font-bold text-slate-950 dark:text-white">
                          {family.familyName || "—"}
                        </LdsTableCell>
                        <LdsTableCell>
                          {parents.map((parent) => parent.name).filter(Boolean).join(", ") || "—"}
                        </LdsTableCell>
                        <LdsTableCell>{(family.children || []).join(", ") || "—"}</LdsTableCell>
                        <LdsTableCell>{formatDutchDate(family.createdAt)}</LdsTableCell>
                        <LdsTableCell>
                          {parents.some((parent) => parent.email || parent.phone)
                            ? parents
                                .filter((parent) => parent.email || parent.phone)
                                .map((parent, index) => (
                                  <div key={index}>
                                    {[parent.email, parent.phone].filter(Boolean).join(" · ")}
                                  </div>
                                ))
                            : "—"}
                        </LdsTableCell>
                        {canOpenFamily && (
                          <LdsTableCell className="text-right">
                            <button
                              type="button"
                              onClick={() => navigate(`/family/${family.familyId}/details`)}
                              className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20 dark:focus:ring-indigo-400/10"
                              aria-label={t("reports.families.inspect_family_label", {
                                family: family.familyName || "",
                                defaultValue: "Inspect family {{family}}",
                              })}
                            >
                              <FaFolderOpen aria-hidden="true" />
                              {t("reports.families.inspect_family", { defaultValue: "View family" })}
                            </button>
                          </LdsTableCell>
                        )}
                      </LdsTableRow>
                    );
                  })
                ) : (
                  <LdsTableRow>
                    <LdsTableCell colSpan={canOpenFamily ? 6 : 5} className="py-12 text-center text-slate-500">
                      {t("reports.new_families.no_results", {
                        defaultValue: "No new families in this period.",
                      })}
                    </LdsTableCell>
                  </LdsTableRow>
                )}
              </LdsTableBody>
            </LdsTable>
          </LdsTableScroll>
        )}
      </LdsTableCard>
    </LdsOverviewPage>
  );
}
